// Big-screen backgrounds for the host display. Mirrors the music pattern in
// audio/tracks.ts: the host picks one at launch and it's remembered per PIN in
// localStorage, then read back when the big screen mounts (HostGame).
//
// Everything here is pure CSS — warm, GAIL's-toned gradients and a couple of
// faint patterns. They're all kept light on purpose so the existing dark ink
// text and white-ish cards stay perfectly readable on top (no scrim needed).
// "none" is the default (the classic cream the app has always used).
export interface Background {
  id: string;
  name: string;
  /** CSS `background` shorthand applied to the full-bleed layer. null = no override. */
  css: string | null;
}

export const BACKGROUNDS: Background[] = [
  { id: "none", name: "Classic cream", css: null },
  {
    id: "warm-glow",
    name: "Warm glow",
    css: "radial-gradient(130% 100% at 50% -12%, #FFF3DC 0%, #FAF4EC 52%, #F3E6CD 100%)",
  },
  {
    id: "golden-hour",
    name: "Golden hour",
    css: "linear-gradient(165deg, #FBF1D6 0%, #FAF4EC 50%, #F5E8CF 100%)",
  },
  {
    id: "rose-dawn",
    name: "Rose dawn",
    css: "radial-gradient(130% 100% at 50% -12%, #FBEDEF 0%, #FAF4EC 50%, #F6E5DE 100%)",
  },
  {
    id: "sage-mist",
    name: "Sage mist",
    css: "linear-gradient(160deg, #EDF2E5 0%, #FAF4EC 55%, #F1ECDF 100%)",
  },
  {
    id: "wheat-dots",
    name: "Wheat dots",
    css: "radial-gradient(circle at 50% 50%, rgba(176,124,60,.08) 0 1.5px, transparent 2px) 0 0 / 26px 26px, #FAF4EC",
  },
  {
    id: "kraft-weave",
    name: "Kraft weave",
    css: "repeating-linear-gradient(135deg, rgba(176,124,60,.05) 0 14px, transparent 14px 28px), #F7EFE0",
  },
];

export const DEFAULT_BG_ID = "none";

/** Resolve an id to a background; unknown/null → "Classic cream" (never throws). */
export function backgroundById(id: string | null | undefined): Background {
  return BACKGROUNDS.find((b) => b.id === id) ?? BACKGROUNDS[0];
}

const keyFor = (pin: string) => `bk_bg_${pin}`;

/** Persist the host's choice for this PIN (mirrors bk_music_<pin>). No-op for "none". */
export function rememberBackground(pin: string, id: string): void {
  try {
    if (id && id !== DEFAULT_BG_ID) localStorage.setItem(keyFor(pin), id);
    else localStorage.removeItem(keyFor(pin));
  } catch {
    /* private mode — the big screen just gets the classic cream this session */
  }
}

/** Read back the background chosen for this PIN (→ "Classic cream" if none/blocked). */
export function backgroundFor(pin: string): Background {
  try {
    return backgroundById(localStorage.getItem(keyFor(pin)));
  } catch {
    return BACKGROUNDS[0];
  }
}
